import React from 'react';
import styles from '../../../styles/ReportsDashboard.module.css';
import RateBar from '../Dashboard/RateBar';
import type { AgencyUserStats } from '../../../utils/agencyStatsAggregation';

interface ComplianceDocumentReportsProps {
  overallStats: any;
  categoriesWithStats: any[];
  agencyUsers: AgencyUserStats[];
}

// Mirrors DocumentReports.tsx's UI, but takes agencyUsers as a prop instead of
// reading it off useManagerContext() — that context is scoped to the logged-in
// manager's own agency, which a compliance officer viewing an arbitrary agency doesn't have.
const ComplianceDocumentReports: React.FC<ComplianceDocumentReportsProps> = ({
  overallStats,
  categoriesWithStats,
  agencyUsers,
}) => {
  return (
    <div>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>📄 Document Compliance</h2>

        <div className={styles.statsRow}>
          {[
            { label: 'Total Items', value: overallStats.totalItems, color: '#2563eb' },
            { label: 'Completed', value: overallStats.completedItems, color: '#10b981' },
            { label: 'Missing', value: overallStats.missingItems, color: '#d97706' },
            { label: 'Expired', value: overallStats.expiredItems, color: '#ef4444' },
            {
              label: 'Completion',
              value: `${overallStats.completionRate.toFixed(2)}%`,
              color:
                overallStats.completionRate >= 90
                  ? '#10b981'
                  : overallStats.completionRate >= 70
                    ? '#2563eb'
                    : '#ef4444',
            },
          ].map(s => (
            <div key={s.label} className={styles.statCard}>
              <span className={styles.statValue} style={{ color: s.color }}>
                {s.value}
              </span>
              <span className={styles.statLabel}>{s.label}</span>
            </div>
          ))}
        </div>

        {categoriesWithStats.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Category</th>
                <th>Total</th>
                <th>Completed</th>
                <th>Missing</th>
                <th>Expired</th>
                <th>Completion</th>
              </tr>
            </thead>
            <tbody>
              {categoriesWithStats.map((cat: any) => (
                <tr key={cat.id}>
                  <td>{cat.name}</td>
                  <td>{cat.stats.totalItems}</td>
                  <td className={styles.good}>{cat.stats.completedItems}</td>
                  <td className={styles.warn}>{cat.stats.missingItems}</td>
                  <td className={styles.bad}>{cat.stats.expiredItems}</td>
                  <td>
                    <RateBar rate={Math.round(cat.stats.completionRate) ?? 0} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {agencyUsers.length > 0 && (
          <>
            <h3 className={styles.subSectionTitle}>Agent Compliance Breakdown</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Email</th>
                  <th>Completion Rate</th>
                </tr>
              </thead>
              <tbody>
                {agencyUsers.map((agent, i) => (
                  <tr key={agent.user.id ?? agent.user.email ?? i}>
                    <td>{agent.user.displayName || 'Agent'}</td>
                    <td>{agent.user.email ?? '—'}</td>
                    <td>
                      <RateBar rate={Math.round(agent.stats.overall.completionRate ?? 0)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </div>
  );
};

export default ComplianceDocumentReports;
